import type { MiddlewareAPI } from 'redux';
import { fg } from '@atlassian/jira-feature-gating';
import type { StandaloneCustomReporter } from '@atlassian/jira-browser-metrics/src/types';
import type { IssueContextServiceActions } from '@atlassian/jira-issue-context-service/src/types.tsx';
import type { ContextPanelItem } from '@atlassian/jira-issue-layout-common-constants/src/index.tsx';
import type { ContextPanel } from '@atlassian/jira-issue-shared-types/src/common/types/context-panel-type.tsx';
import type { Field } from '@atlassian/jira-issue-shared-types/src/common/types/field-type.tsx';
import { CONTEXT_GROUP_INITIAL_OPENED } from '@atlassian/jira-issue-view-common-constants/src/layout.tsx';
import { getChildIssuesLimitExceededAnalyticsCount } from '@atlassian/jira-issue-view-common-types/src/children-issues-type.tsx';
import type { State } from '@atlassian/jira-issue-view-common-types/src/issue-type.tsx';
import {
	EPIC_ISSUES,
	PORTFOLIO_CHILD_ISSUES,
	SUBTASKS,
} from '@atlassian/jira-issue-view-configurations/src/index.tsx';
import {
	getGroupLocalStorageName,
	getGroupsLocalStorage,
} from '@atlassian/jira-issue-view-layout-group/src/services/local-storage/index.tsx';
import { generateContextPanelId } from '@atlassian/jira-issue-view-product-templates-services/src/services/context/visible-hidden/primary-secondary-items/utils.tsx';
import { issueIdSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/context-selector.tsx';
import {
	childIssuesLimitUrlSelector,
	fieldSelector,
	fieldsSelector,
} from '@atlassian/jira-issue-view-store/src/common/state/selectors/field-selector.tsx';
import { isSimplifiedProjectSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/issue-selector.tsx';
import {
	activityPanelsSelector,
	allContentPanelsSelector,
	contextPanelEntitiesSelector,
	glancesSelector,
	issueViewBackgroundScriptsSelector,
} from '@atlassian/jira-issue-view-store/src/ecosystem/ecosystem-extensions-selector.tsx';
import {
	forgeContextPanelsSelector,
	forgePanelsSelector,
	forgeIssueActionsSelector,
	forgeGlancesSelector,
	forgeIssueActivitySelector,
	forgeCustomFieldsSelector,
} from '@atlassian/jira-issue-view-store/src/ecosystem/forge/forge-extensions-selector.tsx';
import { hierarchyLevelSelector } from '@atlassian/jira-issue-view-store/src/issue-field/state/selectors/hierarchy-level-selector.tsx';
import { ecosystemCustomFieldIdsSelector } from '@atlassian/jira-issue-view-store/src/issue-field/state/selectors/layout-items-selector.tsx';
import { issueTypeHierarchyLevelSelector } from '@atlassian/jira-issue-view-store/src/selectors/breadcrumbs-selector.tsx';
import { totalCommentsSelector } from '@atlassian/jira-issue-view-store/src/selectors/comment-selector.tsx';
import { combinedLinkedIssueCountSelector } from '@atlassian/jira-issue-view-store/src/selectors/issue-links-selector.tsx';
import { issueTypesSelector } from '@atlassian/jira-issue-view-store/src/selectors/issue-types-selector.tsx';
import { isSubtaskTypesPresentSelector } from '@atlassian/jira-issue-view-store/src/selectors/subtask-types-selector.tsx';
import { subtaskEntitiesSelector } from '@atlassian/jira-issue-view-store/src/selectors/subtasks-selector.tsx';
import {
	CASCADING_SELECT_CF_TYPE,
	CHILDREN_ISSUES,
	PROFORMA_FORMS,
	TEAMS_PLATFORM_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import { COMMENTS } from '@atlassian/jira-platform-issue-permalinks/src/constants.tsx';
import { getPermalinkTypeAndId } from '@atlassian/jira-platform-issue-permalinks/src/index.tsx';

const getCascadingSelectFields = (state: State) =>
	Object.values(fieldsSelector(state)).filter(
		(fieldValues: Field) => fieldValues.schema.custom === CASCADING_SELECT_CF_TYPE,
	);

const getAtlassianTeamFields = (state: State) =>
	Object.values(fieldsSelector(state)).filter(
		(fieldValues: Field) => fieldValues.schema.custom === TEAMS_PLATFORM_CF_TYPE,
	);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getCascadingSelectOptionChildrenCount = (cascadingSelectFieldValue: any[]): number => {
	if (cascadingSelectFieldValue.length === 0) {
		return 0;
	}
	return Object.values(cascadingSelectFieldValue).reduce(
		(accumulatedCount, parentValue) => accumulatedCount + (parentValue?.children?.length || 1),
		0,
	);
};

const getCascadingSelectOptionsTotalCount = (state: State): number => {
	const cascadingSelectFields = getCascadingSelectFields(state);
	if (cascadingSelectFields.length === 0) {
		return 0;
	}
	return cascadingSelectFields.reduce(
		(accumulatedCount, cascadingSelectField) =>
			accumulatedCount +
			(getCascadingSelectOptionChildrenCount(cascadingSelectField.allowedValues) || 1),
		0,
	);
};

/**
 * This calculates how many context panels are open and closed by accessing their state in local storage and returning
 * open and closed counts. It takes the provided ecosystem and forge installed plugins and removes them from the counts
 * to ensure we are only counting actual panels that are present.
 *
 * This is done so we can see if there is any correlation between TTI and number of panels open.
 *
 * @param projectKey The key of the current issue's project. Used to lookup the context panel states
 * @param ecosystem Installed ecosystem connect context panel plugins
 * @param forge Installed forge issue context panel layout items. This is a different object to ecosytem as all it cares about is ids
 */
export const calculatePanelCounts = ({
	projectKey,
	ecosystem,
	forge,
}: {
	projectKey?: string;
	ecosystem: ContextPanel[];
	forge: ContextPanelItem[];
}) => {
	let open = 0;
	let closed = 0;
	if (projectKey !== undefined) {
		// The context groups store the state of open / closed inside local storage, so we need to get access to that
		// here. It's not held in redux which is why we look it up this way.
		const groupLocalStorageName = getGroupLocalStorageName(projectKey, 'context-group');
		try {
			// It's possible panels are uninstalled and not removed from localstorage, so we need to check they are installed
			// otherwise the analytics may be incorrectly advising how many panels are open
			const installedPanels = [
				...ecosystem.map((item) => `${groupLocalStorageName}-${generateContextPanelId(item)}`),
				...forge.map((item) => `${groupLocalStorageName}-${item.id}`),
			];

			open = Object.values(
				getGroupsLocalStorage(installedPanels, CONTEXT_GROUP_INITIAL_OPENED),
			).filter(Boolean).length;

			// Panels may not exist in localStorage but may be installed, in which case they are closed as this is the default state
			closed = installedPanels.length - open;
		} catch {
			// No action required
		}
	}

	return { open, closed };
};

const getForgeMetadata = (state: State) => {
	if (!fg('report_detailed_connect_forge_to_reactufo')) {
		return null;
	}

	const metadata = {
		'forge:context_panels': forgeContextPanelsSelector(state).length,
		'forge:activities': forgeIssueActivitySelector(state).length,
		'forge:actions': forgeIssueActionsSelector(state).length,
		'forge:panels': forgePanelsSelector(state).length,
		'forge:custom_fields': forgeCustomFieldsSelector(state).length,
		'forge:bg_scripts': issueViewBackgroundScriptsSelector(state).length,
		'forge:glances': forgeGlancesSelector(state).length,
	};

	return metadata;
};

export const issueScaleabilityReporterFactory =
	(
		store: MiddlewareAPI<State>,
		issueContextActions?: IssueContextServiceActions,
	): StandaloneCustomReporter =>
	() => {
		const state = store.getState();
		const { permalinkType } = getPermalinkTypeAndId() || {};
		const childIssues = fieldSelector(CHILDREN_ISSUES)(state);
		const proformaHarmonisation = fieldSelector(PROFORMA_FORMS)(state);

		const ecosystemContextPanels = contextPanelEntitiesSelector(state);

		const { open: contextPanelOpenCount, closed: contextPanelClosedCount } = calculatePanelCounts({
			projectKey: state?.entities?.project?.projectKey,
			ecosystem: ecosystemContextPanels,
			forge: forgeContextPanelsSelector(state),
		});

		const subtaskCount =
			childIssuesLimitUrlSelector(state)(SUBTASKS) && issueContextActions
				? getChildIssuesLimitExceededAnalyticsCount(issueContextActions.getChildIssuesLimit())
				: subtaskEntitiesSelector(state).length;

		const childIssuesCount =
			childIssuesLimitUrlSelector(state)(CHILDREN_ISSUES) && issueContextActions
				? getChildIssuesLimitExceededAnalyticsCount(issueContextActions.getChildIssuesLimit())
				: childIssues?.value.length ?? 0;

		// If a portfolio issue is over the limit we cannot tell because
		// only the subtasks panel is displayed. Once the portfolio issue
		// type hierarchy level is a value > 1 then we can set this to ">100".
		let portfolioChildIssueCount;
		let actualSubtaskCount;
		let actualChildIssueCount;
		let actualEpicIssueCount;
		let epicIssueCount;

		const subtasks = fieldSelector(SUBTASKS)(state);
		const epicIssues = fieldSelector(EPIC_ISSUES)(state);
		const portfolioChildIssues = fieldSelector(PORTFOLIO_CHILD_ISSUES)(state);

		const getChildIssuesLimitUrl = childIssuesLimitUrlSelector(state);
		const isEpicIssuesOverLimit = Boolean(getChildIssuesLimitUrl(EPIC_ISSUES));
		const isOverLimit =
			Boolean(getChildIssuesLimitUrl(SUBTASKS)) ||
			Boolean(getChildIssuesLimitUrl(CHILDREN_ISSUES)) ||
			isEpicIssuesOverLimit;

		if (isOverLimit) {
			actualChildIssueCount = childIssues?.originalContentLength ?? 0;
			actualEpicIssueCount = epicIssues?.originalContentLength ?? 0;
			// If epic issue is over the limit, we exclude the subtasks field
			// from the store. So we need to grab the data from the epicIssues field.
			actualSubtaskCount = isEpicIssuesOverLimit
				? epicIssues?.otherOriginalChildIssuesLength ?? 0
				: subtasks?.originalContentLength ?? 0;

			epicIssueCount =
				isEpicIssuesOverLimit && issueContextActions
					? getChildIssuesLimitExceededAnalyticsCount(issueContextActions.getChildIssuesLimit())
					: epicIssues?.originalContentLength ?? 0;

			// If portfolio issue is over the limit, we exclude the portfolio-child-issues field
			// from the store. So we need to grab the data from the subtasks field.
			portfolioChildIssueCount = subtasks?.otherOriginalChildIssuesLength ?? 0;
		} else {
			actualChildIssueCount = childIssues?.value.length ?? 0;
			actualSubtaskCount = subtasks?.value.length ?? 0;
			actualEpicIssueCount = epicIssues?.value.length ?? 0;
			portfolioChildIssueCount = portfolioChildIssues?.value.length ?? 0;
			epicIssueCount = actualEpicIssueCount;
		}

		let iframeCount = null;
		// This is is to check if the document is undefined in SSR. See https://hello.atlassian.net/wiki/x/jnEv2w
		if (typeof document !== 'undefined') {
			iframeCount = document?.querySelectorAll('iframe')?.length;
		}

		const ecosystemGlances = glancesSelector(state).length;
		const ecosystemContentPanels = allContentPanelsSelector(state).length;
		const ecosystemActivityPanels = activityPanelsSelector(state).length;
		const ecosystemCustomsFields = ecosystemCustomFieldIdsSelector(state).length;

		const ecosystemTeams = ecosystemContextPanels.some(
			(panel) => panel.appKey === 'com.atlassian.atlas.jira',
		);
		const ecosystemAutomation = ecosystemContextPanels.some(
			(panel) => panel.appKey === 'com.codebarrel.addons.automation',
		);

		const ecosystemMetadata = fg('report_detailed_connect_forge_to_reactufo') && {
			'connect:glances': ecosystemGlances,
			'connect:panels': ecosystemContentPanels,
			'connect:activities': ecosystemActivityPanels,
			'connect:context_panels': ecosystemContextPanels.length,
			'connect:custom_fields': ecosystemCustomsFields,
			'connect:has_teams': ecosystemTeams,
			'connect:has_automation': ecosystemAutomation,
		};

		// Properties added here show up in:
		// - jira.fe.custom.issue-view-on-page-load
		// - jira.fe.page-load.issue-view
		// - and jira.fe.custom.embedded-issue-view-load
		// Verify these in the BM3 tab of the Chrome Analytics extension.
		return {
			ecosystemGlances,
			ecosystemContentPanels,
			contextPanelOpenCount,
			contextPanelClosedCount,
			ecosystemActivityPanels,
			...(fg('report_detailed_connect_forge_to_reactufo') ? ecosystemMetadata : {}),
			...(fg('report_detailed_connect_forge_to_reactufo') ? getForgeMetadata(state) : {}),
			iframeCount,
			hasIframe: Boolean(iframeCount),
			ecosystemCustomsFields,
			childIssues: childIssuesCount,
			linkIssueCount: combinedLinkedIssueCountSelector(state),
			subtaskCount,
			actualChildIssueCount,
			actualSubtaskCount,
			/* This is here because we do not currently get exposures for the gate, but if we have it then we
			 can get more accurate expreiment perf results in splunk. clean up with issue_view_ssr_perf_m2 */
			modernSSRRolloutFG: fg('issue-view-ssr-perf-m2'),
			actualEpicIssueCount,
			actualPortfolioChildIssueCount: portfolioChildIssueCount,
			epicIssueCount,
			portfolioChildIssueCount,
			issueId: issueIdSelector(state),
			commentsTotalCount: totalCommentsSelector(state),
			hasCommentPermalink: permalinkType === COMMENTS,
			cascadingSelectCount: getCascadingSelectFields(state).length,
			isAtlassianTeamFieldEnabled: getAtlassianTeamFields(state).length > 0,
			cascadingSelectOptionsTotalCount: getCascadingSelectOptionsTotalCount(state),
			proformaHarmonisationEnabled: proformaHarmonisation
				? proformaHarmonisation.value.isHarmonisationEnabled
				: false,
			hasSubtasksTypes: isSubtaskTypesPresentSelector(state),
			issueTypesCount: issueTypesSelector(state).length,
			issueTypeHierarchyLevel: !isSimplifiedProjectSelector(state)
				? issueTypeHierarchyLevelSelector(state)
				: hierarchyLevelSelector(state),
		};
	};
